import React from 'react';
import { X, Type, AlignLeft, AlignCenter, AlignRight, RotateCw, Trash2, Palette } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TextBoxPropertiesPanel = ({ textBox, onUpdate, onDelete, onClose }) => {
  if (!textBox) return null;

  const handleChange = (field, value) => {
    onUpdate(textBox.id, { [field]: value });
  };

  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Type className="w-4 h-4" />
          Text Box Properties
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Text Content */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm">Text</Label>
        <textarea
          value={textBox.text || ''}
          onChange={(e) => handleChange('text', e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
          placeholder="Enter your text..."
        />
      </div>

      {/* Font Size - Input field instead of slider */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm">Font Size (px)</Label>
        <Input
          type="number"
          value={textBox.font_size || 16}
          onChange={(e) => {
            const val = parseInt(e.target.value) || 16;
            handleChange('font_size', Math.max(8, Math.min(72, val)));
          }}
          className="bg-slate-800 border-slate-700 text-white text-sm"
          min={8}
          max={72}
          placeholder="16"
        />
        <p className="text-xs text-slate-500">Enter size between 8 and 72</p>
      </div>

      {/* Font Color */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm flex items-center gap-1">
          <Palette className="w-3 h-3" />
          Font Color
        </Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={textBox.font_color || '#000000'}
            onChange={(e) => handleChange('font_color', e.target.value)}
            className="w-10 h-10 rounded cursor-pointer border border-slate-700"
          />
          <Input
            value={textBox.font_color || '#000000'}
            onChange={(e) => handleChange('font_color', e.target.value)}
            className="flex-1 bg-slate-800 border-slate-700 text-white font-mono text-sm"
            placeholder="#000000"
          />
        </div>
      </div>

      {/* Background Color */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm flex items-center gap-1">
            <Palette className="w-3 h-3" />
            Background Color
          </Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleChange('background_color', null)}
            className="text-xs text-slate-400 hover:text-white h-6 px-2"
          >
            Clear
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={textBox.background_color || '#ffffff'}
            onChange={(e) => handleChange('background_color', e.target.value)}
            className="w-10 h-10 rounded cursor-pointer border border-slate-700"
          />
          <Input
            value={textBox.background_color || ''}
            onChange={(e) => handleChange('background_color', e.target.value || null)}
            className="flex-1 bg-slate-800 border-slate-700 text-white font-mono text-sm"
            placeholder="transparent"
          />
        </div>
      </div>

      {/* Text Alignment */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm">Alignment</Label>
        <div className="flex gap-2">
          {[
            { value: 'left', icon: AlignLeft, label: 'Left' },
            { value: 'center', icon: AlignCenter, label: 'Center' },
            { value: 'right', icon: AlignRight, label: 'Right' },
          ].map(({ value, icon: Icon, label }) => (
            <Button
              key={value}
              variant={textBox.text_align === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleChange('text_align', value)}
              className={`flex-1 ${textBox.text_align === value 
                ? 'bg-yellow-500 text-slate-900 hover:bg-yellow-600' 
                : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}
              title={label}
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
        </div>
      </div>

      {/* Rotation - Input field */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm flex items-center gap-1">
          <RotateCw className="w-3 h-3" />
          Rotation (degrees)
        </Label>
        <Input
          type="number"
          value={textBox.rotation || 0}
          onChange={(e) => {
            let val = parseInt(e.target.value) || 0;
            val = ((val % 360) + 360) % 360; // Normalize to 0-359
            handleChange('rotation', val);
          }}
          className="bg-slate-800 border-slate-700 text-white text-sm"
          min={0}
          max={359}
          step={15}
          placeholder="0"
        />
      </div>

      {/* Size */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm">Size (pixels)</Label>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Width</Label>
            <Input
              type="number"
              value={textBox.width || 150}
              onChange={(e) => handleChange('width', Math.max(50, parseInt(e.target.value) || 150))}
              className="bg-slate-800 border-slate-700 text-white text-sm"
              min={50}
              max={800}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Height</Label>
            <Input
              type="number"
              value={textBox.height || 40}
              onChange={(e) => handleChange('height', Math.max(20, parseInt(e.target.value) || 40))}
              className="bg-slate-800 border-slate-700 text-white text-sm"
              min={20}
              max={400}
            />
          </div>
        </div>
      </div>

      {/* Delete Button */}
      <Button
        variant="destructive"
        className="w-full mt-4"
        onClick={() => onDelete(textBox.id)}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete Text Box
      </Button>
    </div>
  );
};

export default TextBoxPropertiesPanel;
